/**
 * Chat Service
 *
 * Handles real-time chat functionality using Supabase Realtime.
 * Implements standardized message handling for the simplified data architecture.
 */

import { supabase } from '../supabase';
import { generateChatRoomId } from '../../utils/chatUtils';
import { persistMessage, deserializeMessage } from './messageStore';
import { createCatchUpScheduler } from './catchUp';
// We keep the direct sessionManager imports for backward compatibility
import { getSessionChatRoomId, setSessionChatRoomId } from '../session/sessionManager';
import { getSenderId } from '../../utils/identityUtils';
import { refreshFocusTimestamp } from '../pushNotificationService';

// ============================================================================
// CHANNEL ARCHITECTURE
//
// Each chatRoomId gets one channel:
//   DB channel (${chatRoomId}-db) — postgres_changes subscription on
//   chat_messages. Fires global + component handlers on INSERT/UPDATE.
//
// Component handlers are stored in a module-level registry (componentHandlers).
// joinChatRoom() registers the handler and ensures the DB channel is open.
//
// Presence is handled by useHeartbeat (DB heartbeat RPC), not Supabase
// Realtime presence.
// ============================================================================

// Component handler registry — keyed by chatRoomId.
// Replaces the old _currentMessageHandler delegation pattern where handlers
// were stored on the main chat channel object.
const componentHandlers = {};

export const setComponentHandler = (chatRoomId, handler) => {
  componentHandlers[chatRoomId] = handler;
};

export const clearComponentHandler = (chatRoomId) => {
  delete componentHandlers[chatRoomId];
};

// Store active reconnection cancel functions (module-level for cleanup even when joinChatRoom throws)
const activeReconnections = {};

// Reconnection callback registry — allows callers to re-attach handlers after reconnect
const reconnectionCallbacks = {};

// Visibility handler registry — keyed by chatRoomId, for cleanup on leave/clear
const visibilityHandlers = {};

// Session-level channel tracking — channels in this Set survive component unmounts.
// markSessionChannel() is called synchronously (before React state update) so the Set
// is populated before any cleanup function checks it.
const sessionChannelIds = new Set();

// Global message handler — runs for EVERY message on EVERY channel,
// regardless of which component is mounted.
// Set by ConnectionStateProvider to persist critical data types.
let globalMessageHandler = null;

/**
 * Catch-up (chat-reconnect-catchup): the live feed doesn't replay inserts
 * missed during a gap, so when a gap may have happened — the room's feed
 * subscribed again, the network came back, the page returned to the front —
 * the registered handler re-reads the room from the DB (the page-load path).
 * Coalesced per room; only rooms still in use (roomsInUse).
 */
let catchUpHandler = null;
export const setCatchUpHandler = (handler) => {
  catchUpHandler = handler;
};
// A room still in use: joined (its visibility watcher is registered on join,
// removed on leave) or a session room — even while its feed is down (a CLOSED
// feed drops out of activeDbChannels, which is exactly when catch-up matters).
const roomsInUse = () =>
  new Set([
    ...Object.keys(visibilityHandlers),
    ...sessionChannelIds,
    ...Object.keys(activeDbChannels),
  ]);
const catchUp = createCatchUpScheduler((chatRoomId) => {
  if (catchUpHandler && roomsInUse().has(chatRoomId)) catchUpHandler(chatRoomId);
});
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    roomsInUse().forEach((chatRoomId) => catchUp.request(chatRoomId));
  });
}

export const setGlobalMessageHandler = (handler) => {
  globalMessageHandler = handler;
  console.log(`[CHAT] Global message handler ${handler ? 'registered' : 'unregistered'}`);
};

/**
 * Mark a channel as session-level (survives component unmounts).
 * Called synchronously inside updateSessionChatRoomId() so cleanup
 * functions always see the mark before they run.
 */
export const markSessionChannel = (chatRoomId) => {
  if (!chatRoomId) return;
  sessionChannelIds.add(chatRoomId);
  console.log(`[CHAT] Marked session channel: ${chatRoomId}`);
};

/**
 * Ensure a session channel exists.
 * If the channel is already open, this is a no-op.
 * Called by ConnectionStateContext when sessionChatRoomId changes.
 */
export const openSessionChannel = async (chatRoomId) => {
  if (!chatRoomId) return;
  markSessionChannel(chatRoomId);
  if (activeDbChannels[chatRoomId]) {
    console.log(`[CHAT] Session channel already active: ${chatRoomId}`);
    return;
  }
  try {
    await joinChatRoom(chatRoomId, null);
    console.log(`[CHAT] Opened session channel: ${chatRoomId}`);
  } catch (err) {
    // "Channel was closed" is expected when QRScreen generates a new chatRoomId,
    // closing the old session channel mid-subscribe. Downgrade to log.
    if (err.message === 'Channel was closed') {
      console.log(`[CHAT] Session channel closed (expected on new session): ${chatRoomId}`);
    } else {
      console.error(`[CHAT] Failed to open session channel: ${chatRoomId}`, err);
    }
  }
};

/**
 * Close a session channel and remove it from the tracking Set.
 * Called when sessionChatRoomId changes (cleanup of previous session).
 */
export const closeSessionChannel = (chatRoomId) => {
  if (!chatRoomId) return;
  sessionChannelIds.delete(chatRoomId);
  // Remove visibility handler
  if (visibilityHandlers[chatRoomId]) {
    document.removeEventListener('visibilitychange', visibilityHandlers[chatRoomId]);
    delete visibilityHandlers[chatRoomId];
  }
  closeMessageDeliveryChannel(chatRoomId);
  clearComponentHandler(chatRoomId);
  console.log(`[CHAT] Closed session channel: ${chatRoomId}`);
};

// Message types
export const MESSAGE_TYPES = {
  PROFILE_DATA_REQUEST: 'profile_data_request', // Anonymous user requests profile data
  PROFILE_DATA_RESPONSE: 'profile_data_response', // Profile user responds with data
  ANON_DATA_RESPONSE: 'anon_data_response', // Anonymous user responds with data
  CONTACT: 'contact', // Contact information message
  CHAT: 'chat', // Regular chat message
};

/**
 * Create a new chat room or return existing session chat room
 * This ensures only one chatRoomId is used per session
 *
 * @param {Object} sessionContext - Optional session context from SessionContext
 * @returns {Promise<string>} The chat room ID (either existing session ID or new)
 */
export const createChatRoom = (sessionContext = null) => {
  return new Promise((resolve) => {
    // Check if we have a SessionContext object passed in
    if (sessionContext) {
      const { sessionChatRoomId, updateSessionChatRoomId } = sessionContext;

      // If we have an existing session chatRoomId in context, use it
      if (sessionChatRoomId) {
        console.log(`[CHAT] Using existing session chatRoomId from context: ${sessionChatRoomId}`);
        resolve(sessionChatRoomId);
        return;
      }

      // If no session chatRoomId in context, generate a new one and set it in context
      const chatRoomId = generateChatRoomId();
      updateSessionChatRoomId(chatRoomId);
      console.log(`[CHAT] Created new session chatRoomId in context: ${chatRoomId}`);
      resolve(chatRoomId);
      return;
    }

    // Fallback to direct sessionStorage access for backward compatibility
    // First check if we already have a session chatRoomId
    const sessionChatRoomId = getSessionChatRoomId();

    if (sessionChatRoomId) {
      console.log(`[CHAT] Using existing session chatRoomId from storage: ${sessionChatRoomId}`);
      resolve(sessionChatRoomId);
      return;
    }

    // If no session chatRoomId exists, generate a new one and set it
    const chatRoomId = generateChatRoomId();
    setSessionChatRoomId(chatRoomId);
    console.log(`[CHAT] Created new session chatRoomId in storage: ${chatRoomId}`);
    resolve(chatRoomId);
  });
};

/**
 * Higher-order function that provides SessionContext to createChatRoom
 * This allows components that use SessionContext to easily create a chat room
 * while maintaining session consistency
 *
 * @param {Function} useSessionHook - The useSession hook from SessionContext
 * @returns {Function} A function that creates a chat room using the session context
 */
export const createChatRoomWithSession = (useSessionHook) => {
  return () => {
    const sessionContext = useSessionHook();
    return createChatRoom(sessionContext);
  };
};

/**
 * DB delivery channel — postgres_changes subscription on chat_messages.
 * This is the only real-time channel per connection.
 */
const activeDbChannels = {};

const handleMessageChange = (chatRoomId) => (payload) => {
  const row = payload.new;
  if (!row) return;

  // Filter self-sent messages — sender already has the data
  if (row.sender_id === getSenderId()) return;

  const message = deserializeMessage(row);
  message.chatRoomId = chatRoomId;

  // Global handler first (data persistence, survives navigation)
  if (globalMessageHandler) {
    try {
      globalMessageHandler(message);
    } catch (err) {
      console.error('[CHAT] Global handler error (postgres_changes):', err);
    }
  }

  // Component handler (UI updates) — from the handler registry
  const handler = componentHandlers[chatRoomId];
  if (handler) {
    handler(message);
  }
};

/**
 * Open the DB delivery channel for a chatRoomId.
 * Returns a promise that resolves when the channel reaches SUBSCRIBED status.
 * If the channel already exists, returns the existing subscribe promise
 * (resolves immediately if already subscribed).
 */
const openMessageDeliveryChannel = (chatRoomId, { reopen = false } = {}) => {
  // If channel already exists, return its subscribe promise (resolved = instant)
  if (activeDbChannels[chatRoomId]) {
    return activeDbChannels[chatRoomId]._subscribePromise || Promise.resolve();
  }

  const pgConfig = {
    schema: 'public',
    table: 'chat_messages',
    filter: `chat_room_id=eq.${chatRoomId}`,
  };

  const SUBSCRIBE_TIMEOUT = 30000; // 30 seconds

  // Hoist dbChannel so we can attach the promise after the constructor
  let dbChannel;

  // Any SUBSCRIBED after the first (a rejoin), or the first of a reopened
  // channel, follows a gap the feed won't replay → catch up.
  let subscribedBefore = false;

  const subscribePromise = new Promise((resolve, reject) => {
    dbChannel = supabase
      .channel(`${chatRoomId}-db`)
      .on('postgres_changes', { event: 'INSERT', ...pgConfig }, handleMessageChange(chatRoomId))
      .on('postgres_changes', { event: 'UPDATE', ...pgConfig }, handleMessageChange(chatRoomId))
      .subscribe((status) => {
        console.log(`[CHAT] DB channel status: ${status} for chatRoomId: ${chatRoomId}`);

        if (status === 'SUBSCRIBED') {
          console.log(`[CHAT] DB channel subscribed: ${chatRoomId}`);
          if (subscribedBefore || reopen) catchUp.request(chatRoomId);
          subscribedBefore = true;
          resolve();
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`[CHAT] DB channel error: ${chatRoomId}`);
          reject(new Error('Channel subscription failed'));
        } else if (status === 'TIMED_OUT') {
          console.error(`[CHAT] DB channel timed out: ${chatRoomId}`);
          reject(new Error('Channel subscription timed out'));
        } else if (status === 'CLOSED') {
          console.log(`[CHAT] DB channel closed: ${chatRoomId}`);
          // Only clean up if this is still the active channel (not replaced by a new one)
          if (activeDbChannels[chatRoomId] === dbChannel) {
            delete activeDbChannels[chatRoomId];
          }
          reject(new Error('Channel was closed'));
        }
      }, SUBSCRIBE_TIMEOUT);

    // Store channel in map immediately to prevent duplicate creation
    activeDbChannels[chatRoomId] = dbChannel;
  });

  // Attach promise AFTER constructor (can't reference subscribePromise inside its own constructor)
  dbChannel._subscribePromise = subscribePromise;

  return subscribePromise;
};

const closeMessageDeliveryChannel = (chatRoomId) => {
  catchUp.cancel(chatRoomId);
  const dbChannel = activeDbChannels[chatRoomId];
  if (dbChannel) {
    dbChannel.unsubscribe();
    delete activeDbChannels[chatRoomId];
  }
};

/**
 * Check if the DB delivery channel for a chatRoomId is healthy.
 * Replaces the old getActiveChannel() which checked the main chat channel.
 * @param {string} chatRoomId - The chat room ID
 * @returns {boolean} True if the DB channel exists and is in a healthy state
 */
export const isChannelHealthy = (chatRoomId) => {
  const dbChannel = activeDbChannels[chatRoomId];
  if (!dbChannel) return false;
  return dbChannel.state === 'joined' || dbChannel.state === 'joined_once';
};

/**
 * Implement exponential backoff for reconnection of the DB channel.
 * Returns a cancel function that can be called to stop the reconnection attempts.
 * @param {string} chatRoomId - The chat room ID
 * @returns {Function} Cancel function to stop reconnection attempts
 */
const attemptReconnect = (chatRoomId) => {
  let cancelled = false;

  // Create cancel function
  const cancel = () => {
    cancelled = true;
    console.log(`[CHAT] Cancelling reconnection for chatRoomId: ${chatRoomId}`);
    // Remove from active reconnections registry
    delete activeReconnections[chatRoomId];
  };

  // Register in module-level registry so it can be cancelled even if joinChatRoom throws
  activeReconnections[chatRoomId] = cancel;

  const run = async () => {
    if (!chatRoomId) {
      console.error('[CHAT] Missing chatRoomId for reconnection');
      delete activeReconnections[chatRoomId];
      return false;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const baseDelay = 1000; // 1 second

    while (attempts < maxAttempts) {
      // Check for cancellation before starting attempt
      if (cancelled) {
        console.log(`[CHAT] Reconnection cancelled for chatRoomId: ${chatRoomId}`);
        delete activeReconnections[chatRoomId];
        return false;
      }

      attempts++;
      const delay = baseDelay * Math.pow(2, attempts - 1); // Exponential backoff

      console.log(
        `[CHAT] Reconnection attempt ${attempts}/${maxAttempts} for chatRoomId: ${chatRoomId} in ${delay}ms`
      );

      await new Promise((resolve) => setTimeout(resolve, delay));

      // Check for cancellation after delay (in case cancelled during wait)
      if (cancelled) {
        console.log(`[CHAT] Reconnection cancelled for chatRoomId: ${chatRoomId}`);
        delete activeReconnections[chatRoomId];
        return false;
      }

      try {
        // Close and reopen the DB channel
        closeMessageDeliveryChannel(chatRoomId);
        await openMessageDeliveryChannel(chatRoomId, { reopen: true });

        // Check for cancellation after reopen
        if (cancelled) {
          console.log(`[CHAT] Reconnection cancelled after reopen for chatRoomId: ${chatRoomId}`);
          closeMessageDeliveryChannel(chatRoomId);
          delete activeReconnections[chatRoomId];
          return false;
        }

        console.log(`[CHAT] Successfully reconnected DB channel for chatRoomId: ${chatRoomId}`);
        // Notify registered callbacks (e.g., QRScreen re-attaching handlers)
        if (reconnectionCallbacks[chatRoomId]) {
          try {
            reconnectionCallbacks[chatRoomId]();
          } catch (cbError) {
            console.error(`[CHAT] Reconnection callback error for ${chatRoomId}:`, cbError);
          }
        }
        // Clean up registry on success
        delete activeReconnections[chatRoomId];
        return true;
      } catch (error) {
        console.error(
          `[CHAT] Reconnection attempt ${attempts} failed for chatRoomId: ${chatRoomId}`,
          error
        );
      }
    }

    console.error(
      `[CHAT] Failed to reconnect after ${maxAttempts} attempts for chatRoomId: ${chatRoomId}`
    );
    // Clean up registry on failure
    delete activeReconnections[chatRoomId];
    return false;
  };

  // Start the reconnection process
  run();

  // Return cancel function
  return cancel;
};

/**
 * Join a chat room — registers a component handler, ensures the DB channel
 * is open, and sets up visibility monitoring for reconnection.
 *
 * @param {string} chatRoomId - The ID of the chat room
 * @param {Function} onMessage - Callback for new messages (null for session-only channels)
 * @returns {Function} Cleanup function to unsubscribe
 */
export const joinChatRoom = async (chatRoomId, onMessage) => {
  if (!chatRoomId) {
    console.error('[CHAT] Missing chatRoomId for joinChatRoom');
    throw new Error('Missing chatRoomId');
  }

  // CRITICAL: Cancel ALL existing reconnections before starting new connection
  // This prevents old reconnection attempts from competing with new connections
  const existingReconnections = Object.keys(activeReconnections);
  if (existingReconnections.length > 0) {
    console.log(
      `[CHAT] Cancelling ${existingReconnections.length} existing reconnection(s) before joining: ${chatRoomId}`
    );
    existingReconnections.forEach((oldChatRoomId) => {
      if (activeReconnections[oldChatRoomId]) {
        activeReconnections[oldChatRoomId]();
      }
    });
  }

  // Register component handler in the registry
  if (onMessage) {
    console.log(`[CHAT] Registering component handler for chatRoomId: ${chatRoomId}`);
    componentHandlers[chatRoomId] = onMessage;
  }

  // If DB channel already exists, just swap handler (reuse path)
  if (activeDbChannels[chatRoomId]) {
    console.log(`[CHAT] Reusing existing DB channel for chatRoomId: ${chatRoomId}`);

    // Wait for channel to finish subscribing (if still in progress).
    // openSessionChannel stores channels in activeDbChannels BEFORE subscribe completes.
    const pending = activeDbChannels[chatRoomId]._subscribePromise;
    if (pending) {
      await pending;
    }

    return createCleanupFunction(chatRoomId);
  }

  // Open DB channel and await subscription
  try {
    console.log(`[CHAT] Opening DB channel for chatRoomId: ${chatRoomId}`);
    await openMessageDeliveryChannel(chatRoomId);
    console.log(`[CHAT] Successfully joined chatRoomId: ${chatRoomId}`);
  } catch (error) {
    // "Channel was closed" is expected when a new session replaces the old one
    // (e.g., QR screen generates new chatRoomId while old channel is mid-subscribe).
    if (error.message === 'Channel was closed') {
      console.log(`[CHAT] Channel closed during join (expected on session change): ${chatRoomId}`);
    } else {
      console.error(`[CHAT] Error joining chatRoomId: ${chatRoomId}`, error);
      // Start background reconnection for timeout/error
      attemptReconnect(chatRoomId);
    }
    throw error;
  }

  // Set up visibility monitoring on the DB channel (if not already set up)
  if (!visibilityHandlers[chatRoomId]) {
    const visibilityHandler = async () => {
      if (document.visibilityState === 'visible') {
        // Back in front: a short gap can leave the feed looking healthy while
        // it missed inserts (chat-reconnect-catchup run 2) → catch up anyway.
        catchUp.request(chatRoomId);
        const healthy = isChannelHealthy(chatRoomId);

        if (!healthy) {
          if (activeReconnections[chatRoomId]) {
            console.log(
              `[CHAT] Visibility change: reconnection already in progress for ${chatRoomId}, skipping`
            );
          } else {
            console.log(
              `[CHAT] Visibility change: DB channel unhealthy, triggering reconnect for ${chatRoomId}`
            );
            attemptReconnect(chatRoomId);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    visibilityHandlers[chatRoomId] = visibilityHandler;
  }

  return createCleanupFunction(chatRoomId);
};

/**
 * Create a cleanup function for a chat room connection.
 * Session channels preserve the DB channel and visibility handler;
 * non-session channels do full cleanup.
 */
const createCleanupFunction = (chatRoomId) => {
  return () => {
    console.log('Cleanup called for chat room:', chatRoomId);

    // SESSION CHANNEL: Preserve the DB channel and visibility handler.
    // Clear component handler so only the global handler keeps receiving.
    if (sessionChannelIds.has(chatRoomId)) {
      clearComponentHandler(chatRoomId);
      return; // Don't cancel reconnect, don't remove visibilityHandler, don't close channel
    }

    // Non-session channels: full cleanup including reconnection
    if (activeReconnections[chatRoomId]) {
      activeReconnections[chatRoomId]();
    }

    // Remove visibility handler
    if (visibilityHandlers[chatRoomId]) {
      document.removeEventListener('visibilitychange', visibilityHandlers[chatRoomId]);
      delete visibilityHandlers[chatRoomId];
      console.log(`[CHAT] Removed visibility handler for ${chatRoomId}`);
    }

    // Clear handler and close channel
    clearComponentHandler(chatRoomId);
    closeMessageDeliveryChannel(chatRoomId);
  };
};

/**
 * Register a callback to be called after a channel successfully reconnects.
 *
 * @param {string} chatRoomId - The chat room ID to watch
 * @param {Function} callback - Called after successful reconnection
 * @returns {Function} Unregister function
 */
export const onReconnected = (chatRoomId, callback) => {
  reconnectionCallbacks[chatRoomId] = callback;
  return () => {
    delete reconnectionCallbacks[chatRoomId];
  };
};

/**
 * Send a message to a chat room
 * @param {string} chatRoomId - The ID of the chat room
 * @param {Object} message - The message to send (must include type, sender, and chatRoomId)
 * @returns {Promise<Object>} The result of the send operation
 */
// Deterministic IDs for data messages — re-shares upsert the same DB row
const DATA_ID_PATTERNS = {
  // A repeat connect's handshake reply gets its OWN row: sharing the card's id let
  // it overwrite the full shared card with the name+photo handshake, which recovery
  // skips — a fresh device then lost the card (b/meetings-reconnect S3).
  profile_data_response: (msg) =>
    msg.isReconnection
      ? `pdr_rc_${msg.chatRoomId}_${msg.senderProfileUserId}`
      : `pdr_${msg.chatRoomId}_${msg.senderProfileUserId}`,
  metadata_updated: (msg) => `meta_${msg.chatRoomId}_${msg.senderProfileUserId}`,
  contact: (msg) => `contact_${msg.chatRoomId}_${msg.senderId}`,
  profile_exchange: (msg) => `pxp_${msg.chatRoomId}_${msg.senderProfileUserId}`,
};

/**
 * How long a guest's save — a message, or ⚡ Connect's mint — may wait before
 * the page says so with the "Lost connection" card. One value for both
 * (Mark, 2026-10-07).
 */
export const GUEST_SAVE_SLOW_MS = 6000;

export const sendMessage = async (chatRoomId, message) => {
  if (!chatRoomId) {
    console.error('[CHAT] Missing chatRoomId for message');
    return { error: 'Missing chatRoomId' };
  }

  if (!message || !message.type) {
    console.error('[CHAT] Invalid message format', message);
    return { error: 'Invalid message format' };
  }

  const messageToSend = {
    ...message,
    chatRoomId,
    timestamp: message.timestamp || new Date().toISOString(),
    senderId: message.senderId || getSenderId(),
  };

  // Data message IDs are deterministic — re-shares overwrite same row
  const idPattern = DATA_ID_PATTERNS[messageToSend.type];
  if (idPattern) {
    messageToSend.id = idPattern(messageToSend);
  }

  console.log(`[CHAT] sendMessage: ${messageToSend.type} for ${chatRoomId}`);

  // 1. DB write FIRST — this is the delivery
  if (messageToSend.id) {
    // A guest's write that hasn't landed is said on screen, never silent
    // (guest-session-loss, Mark 2026-10-07): refused → the card at once;
    // still waiting after GUEST_SAVE_SLOW_MS (offline: the network wrapper
    // keeps retrying) → the card, which clears itself when the write lands.
    const isGuest = String(messageToSend.senderId || '').startsWith('anon_');
    const say = (name, detail) =>
      isGuest && window.dispatchEvent(new CustomEvent(name, { detail: { chatRoomId, ...detail } }));
    const slow = isGuest
      ? setTimeout(() => say('guestSaveFailed', { message: messageToSend }), GUEST_SAVE_SLOW_MS)
      : null;
    const success = await persistMessage(chatRoomId, messageToSend);
    clearTimeout(slow);
    if (!success) {
      say('guestSaveFailed', { message: messageToSend });
      return { error: 'DB write failed' };
    }
    say('guestSaveLanded', { messageId: messageToSend.id });
  } else {
    console.warn(
      '[CHAT] sendMessage called without id — message will not persist:',
      messageToSend.type
    );
  }

  // 2. Delivery is handled by postgres_changes — the DB INSERT above triggers
  // a notification on the recipient's channel. No broadcast needed.
  // The recipient's openMessageDeliveryChannel() listener fires, deserializes the row,
  // and calls global + component handlers.

  // 3. Refresh focus timestamp — keeps app_focused_at current during active chatting
  // so server-side suppression doesn't expire while user is sending messages.
  // Fire-and-forget: no await, no-op for anon users (guard inside refreshFocusTimestamp).
  if (messageToSend.type === 'chat') {
    refreshFocusTimestamp();
  }

  return { status: 'ok' };
};

// Legacy message types have been removed in favor of the standardized message structure
// All messages now follow the same format with type, sender, chatRoomId, and timestamp

/**
 * Leave a chat room
 * @param {string} chatRoomId - The chat room ID to leave
 * @returns {Promise<boolean>} True if left successfully
 */
export const leaveChatRoom = async (chatRoomId) => {
  if (!chatRoomId) {
    console.error('[CHAT] Missing chatRoomId for leaveChatRoom');
    return false;
  }

  try {
    console.log(`[CHAT] Leaving chatRoomId: ${chatRoomId}`);

    // Cancel any active reconnection
    if (activeReconnections[chatRoomId]) {
      activeReconnections[chatRoomId]();
    }

    // Remove visibility handler
    if (visibilityHandlers[chatRoomId]) {
      document.removeEventListener('visibilitychange', visibilityHandlers[chatRoomId]);
      delete visibilityHandlers[chatRoomId];
    }

    // Clear handler and close channel
    clearComponentHandler(chatRoomId);
    closeMessageDeliveryChannel(chatRoomId);

    // Clear session storage for this chat room
    try {
      if (sessionStorage.getItem('lastActiveChatRoom') === chatRoomId) {
        sessionStorage.removeItem('lastActiveChatRoom');
        sessionStorage.removeItem('lastPresenceData');
      }
    } catch (e) {
      console.error(`[CHAT] Error clearing session storage for chatRoomId: ${chatRoomId}`, e);
    }

    return true;
  } catch (error) {
    console.error(`[CHAT] Error leaving chatRoomId: ${chatRoomId}`, error);
    return false;
  }
};

/**
 * Clear all active channel subscriptions
 * Used when clearing all data to ensure no orphaned connections remain
 * @returns {Promise<boolean>} Success status
 */
export const clearAllChannels = async () => {
  try {
    sessionChannelIds.clear();

    // Clear all component handlers
    Object.keys(componentHandlers).forEach((id) => delete componentHandlers[id]);

    // Remove all visibility handlers
    Object.keys(visibilityHandlers).forEach((chatRoomId) => {
      document.removeEventListener('visibilitychange', visibilityHandlers[chatRoomId]);
      delete visibilityHandlers[chatRoomId];
    });

    // Close all DB channels
    const channelIds = Object.keys(activeDbChannels);
    console.log(`[CHAT] Clearing all channels: ${channelIds.length} active`);

    for (const chatRoomId of channelIds) {
      try {
        activeDbChannels[chatRoomId].unsubscribe();
        console.log(`[CHAT] Unsubscribed from channel: ${chatRoomId}`);
      } catch (e) {
        console.error(`[CHAT] Error unsubscribing from ${chatRoomId}:`, e);
      }
      delete activeDbChannels[chatRoomId];
    }

    console.log('[CHAT] All channels cleared');
    return true;
  } catch (error) {
    console.error('[CHAT] Error clearing all channels:', error);
    return false;
  }
};
